/**
 * ControlledOverflow Component
 */

import React, { useCallback, useState, useEffect, useRef, useMemo } from 'react';
import { useTheme } from 'styled-components';
import { ControlledOverflowProps } from './controlledOverflow.types';
import {
  LeftButton,
  RightButton,
  LeftShadow,
  RightShadow,
  InnerWrapper,
  OuterWrapper,
  PaginationWrapper,
  PaginationButton,
  CONTROLLED_OVERFLOW,
  EndInsert,
} from './controlledOverflow.style';
import COLORS from '../../style/universal.color';
import { ButtonStyles } from '../Button/button.types';
import { animate } from 'framer-motion';
import BREAKPOINTS from '../../style/breakpoints';

const { mlbWhite, mlbGrey5, linkBlue } = COLORS;

const SCROLL = 'scroll';
const SCROLL_NEXT_CARDS = 'scrollNextCards';
const SCROLL_PREV_CARDS = 'scrollPreviousCards';
const noop = function () {};

let scrollBehaviorBrowserCapable: boolean = false;
let isTouchEnabled: boolean = false;

if (typeof document !== 'undefined' && document.documentElement) {
  scrollBehaviorBrowserCapable = 'scrollBehavior' in document.documentElement.style;
}

if (
  typeof window !== 'undefined' &&
  typeof navigator !== 'undefined' &&
  ('ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    navigator.msMaxTouchPoints > 0)
) {
  isTouchEnabled = true;
}

const getWidth = () => {
  if (typeof window !== 'undefined') {
    return window.innerWidth || 0;
  } else {
    return 0;
  }
};

// Build pagination intervals. These are the values each pagination will send the user
const buildPagIntervals = (
  numPagDots: number,
  pagDotInterval: number,
  partialCardOffset: number,
  cardsPerView: number,
  cardWidth: number,
) => {
  const intervals: number[] = [];
  for (let i: number = 0; i < numPagDots; i++) {
    // The values must be 1 for the left-hand side (or the scrollTo animation won't think it's moving)
    const pagOffset: number = pagDotInterval * i;
    let destination: number = pagOffset || 1;
    if (i > 0) {
      // Kind of tricky.
      // For smooth scrolling, we'll want to offset the destination
      // by any additional cards in view that we might not be accounting for plus
      // the remainder of cards that didn't fit fully in view but will have an
      // impact on scrolling
      const extraCardSideOffset: number = cardsPerView > 1 ? cardWidth / 2 : 0;
      destination = pagDotInterval * i - (extraCardSideOffset + partialCardOffset / 2);
    }
    intervals.push(destination);
  }
  return intervals;
};

const ControlledOverflow = ({
  backgroundColor = mlbWhite,
  childWidth = 185,
  children,
  disablePagination = true,
  mobileBreakpoint = BREAKPOINTS.sm,
  className,
  pageLabel = 'Page',
  disableArrows = false,
  disableShadows = false,
  buttonHeight = 40,
  disableButtonsOnTouch = false,
  nextButtonLabel = 'Next',
  previousButtonLabel = 'Previous',
  shadowArrowScrollOffsetOverride = 0,
  buttonsOnSide = false,
  animationDuration = 0.3,
  buttonSideMargin = 16,
  initCallBack = noop,
  addEndInsert = false,
  endInsertWidth = 16,
  resizeHandler,
  leftButtonIconName = 'carousel-arrow-left',
  rightButtonIconName = 'carousel-arrow-right',
}: ControlledOverflowProps) => {
  // Used if we're actively scrolling to a spot (through arrows or pagination)
  const [scrollingTo, setScrollingTo] = useState(0);

  // State for determining whether to hide the left and right edges with shadow gradients
  const [hideRight, setHideRight] = useState(false);
  const [hideLeft, setHideLeft] = useState(false);

  // Refs for above state so event handlers below can receive correct current info
  const hideRightRef = useRef(hideRight);
  const hideLeftRef = useRef(hideLeft);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const endInsertRef = useRef<HTMLDivElement>(null);

  // The currently active pagination dot index
  const [activeDot, setActiveDot] = useState(0);

  // A value used to determine if the page is resized
  const [resizeWidth, setResizeWidth] = useState(getWidth());

  // Dot intervals used for building the pagination.
  const [dotIntervals, setDotIntervals] = useState<number[]>([]);
  const dotIntervalsRef = useRef(dotIntervals);

  const [showSideButtons, setShowSideButtons] = useState(false);

  const shadowNeededOffset: number =
    typeof shadowArrowScrollOffsetOverride !== 'undefined' ? shadowArrowScrollOffsetOverride : childWidth / 4;

  const { dark = false } = useTheme();
  const numberOfChildren = React.Children.count(children);

  const [calledCallback, setCalledCallback] = useState(false);

  let request: number;
  const scrollListener = (event: Event) => {
    const scrollEl = event.target as HTMLElement;
    if (request) {
      cancelAnimationFrame(request);
    }
    request = requestAnimationFrame(() => {
      calculateScrollValues(scrollEl);
    });
  };

  // Called by the scroll event listener.
  // This is triggered by both manually scrolling and side buttons
  const calculateScrollValues = useCallback((scrollEl: HTMLElement) => {
    // Receive the scroll variables from the card container
    const { scrollLeft, scrollWidth, clientWidth } = scrollEl;

    // Determine if the sides are currently obscured by the shadows
    const leftHidden: boolean = hideLeftRef.current;
    const rightHidden: boolean = hideRightRef.current;

    // Determine the active pagination dot
    let tempDotIndex: number = 0;
    const dotInterv: number[] = dotIntervalsRef.current || [];
    for (let i: number = 1; i < dotInterv.length; i++) {
      if (scrollLeft >= dotInterv[i] - 10) {
        // If the dot is greater than the interval (minus an arbitrary gutter for snapping)
        // use that dot as the active (until maybe the next proves true, etc.)
        tempDotIndex = i;
      }
    }
    // If the scroll position is close enough to the right-hand side, default to the last
    // dot
    if (scrollWidth === scrollLeft + clientWidth) {
      tempDotIndex = dotInterv.length - 1;
    }
    setActiveDot(tempDotIndex);

    // Using the new scroll position, set a new state for the snadows
    const nearLeftBorder: boolean = scrollLeft === 0; // shadowNeededOffset;
    if (!nearLeftBorder && !leftHidden) {
      hideLeftRef.current = true;
      setHideLeft(true);
    } else if (leftHidden && nearLeftBorder) {
      hideLeftRef.current = false;
      setHideLeft(false);
    }

    const nearRightBorder: boolean = scrollWidth - (scrollLeft + clientWidth) <= shadowNeededOffset;
    if (!nearRightBorder && !rightHidden) {
      hideRightRef.current = true;
      setHideRight(true);
    } else if (rightHidden && nearRightBorder) {
      hideRightRef.current = false;
      setHideRight(false);
    }
  }, []);

  useEffect(() => {
    const container = scrollContainerRef.current;

    /** Reset the Scroll offset if number of children changes.
    Fixes the bug where P13n Content getting inserted at the beginning of the
    list was staying partially hidden */
    if (container) {
      container.scrollLeft = 0;
    }
  }, [numberOfChildren]);

  useEffect(() => {
    // Set a scroll event listener that's removed on component dismount
    const container = scrollContainerRef.current;

    if (container && typeof window !== 'undefined') {
      container.addEventListener(SCROLL, scrollListener);
      const { scrollLeft, clientWidth } = container;
      // Calculate the totalwidth of the card.
      const cardWidth = childWidth;
      const innerWrapperWidth =
        clientWidth + parseFloat(window.getComputedStyle(container).getPropertyValue('margin-right'));

      const containerChildren = container.children || [];
      let calcScrollWidth = 0;
      for (let i = 0; i < containerChildren.length; i++) {
        const element = containerChildren[i];
        const elementWidth = parseFloat(element.getBoundingClientRect().width.toString());
        const styles = window && window.getComputedStyle(element);
        const margin =
          parseFloat(styles.getPropertyValue('margin-left')) + parseFloat(styles.getPropertyValue('margin-right'));
        calcScrollWidth += elementWidth + margin;
      }
      if (calcScrollWidth > innerWrapperWidth && !showSideButtons) {
        setShowSideButtons(true);
      } else if (calcScrollWidth <= innerWrapperWidth && showSideButtons) {
        setShowSideButtons(false);
      }

      // If there is room to scroll, initialize the pagination at the bottom
      if (calcScrollWidth > clientWidth && !disablePagination) {
        const cardsPerWidth = clientWidth / cardWidth;

        // Determine appropriate cards to shift per pagination dot
        const cardsToMove = Math.floor(cardsPerWidth - 0.75) || 1;
        // Determine the amount in pixels to shift per pagination dot
        const interval = cardsToMove * cardWidth;
        // Determine the number of dots to show
        const numberOfDots = Math.ceil(calcScrollWidth / interval);
        // Determine the number of cards in view at a time
        const cardsInView = Math.ceil(cardsPerWidth) - 1 || 1;
        // Establish the array of intervals in which the pagination dots will be set up
        const intervals = buildPagIntervals(numberOfDots, interval, clientWidth % interval, cardsInView, cardWidth);
        setDotIntervals(intervals);
        dotIntervalsRef.current = intervals;
      } else {
        // Set dot intervals (that determine pagination) to empty if not worrying about pagination
        setDotIntervals([]);
        dotIntervalsRef.current = [];
      }

      // Determine initial need for a right shadow (if there are more cards
      // than can fit the width)
      if (calcScrollWidth - (scrollLeft + clientWidth) > shadowNeededOffset) {
        hideRightRef.current = true;
        setHideRight(true);
      } else {
        hideRightRef.current = false;
        setHideRight(false);
      }
      return () => {
        container.removeEventListener(SCROLL, scrollListener);
      };
    }
    return;
  }, [calculateScrollValues, resizeWidth, numberOfChildren]);

  // Add a resize listener.
  // Alters a pretty much unused number to retrigger shadow initialization in useEffect
  useEffect(() => {
    let timeoutId: NodeJS.Timeout | string | number | undefined;
    const resizeListener = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(function () {
        setResizeWidth(getWidth());
        if (resizeHandler) {
          const scrollContainer = scrollContainerRef.current;
          resizeHandler({ scrollContainer, endInsert: endInsertRef?.current });
        }
      }, 200);
    };
    window.addEventListener('resize', resizeListener);

    return () => {
      window.removeEventListener('resize', resizeListener);
    };
  }, [resizeHandler]);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;

    if (scrollContainer) {
      if (!calledCallback) {
        initCallBack({ scrollContainer, endInsert: endInsertRef?.current });
        setCalledCallback(true);
      }

      if (scrollingTo !== 0) {
        animate(scrollContainer.scrollLeft, scrollingTo, {
          type: 'tween',
          duration: animationDuration,
          onComplete: () => {
            setScrollingTo(0);
          },
          onUpdate: (v) => {
            scrollContainer.scrollLeft = v;
          },
        });
      }
    }
  }, [scrollingTo, initCallBack]);

  // Handle the side button clicks
  // This will move the scroll position enough for the next new cards to reveal themselves
  const buttonHandlers = useMemo(
    () => ({
      onClick: (evt: React.MouseEvent) => {
        if (scrollingTo === 0 && typeof document !== 'undefined') {
          const buttonId = (evt?.target as HTMLElement)?.id || '';
          const scrollContainer = scrollContainerRef.current;
          if (buttonId && scrollContainer) {
            // Determine the leftmost and rightmost visible elements to the user and use their values to determine scroll distancces
            const { clientWidth = 0, children: scrollChildren = [], scrollLeft = 0 } = scrollContainer;
            let leftmost = null;
            let rightmost = null;

            for (let i = 0; i < scrollChildren.length; i++) {
              const el = scrollChildren[i];
              const bb = el.getBoundingClientRect();
              const { x, width } = bb;
              if (!leftmost && x + width > 0) {
                leftmost = bb;
              }
              if (!rightmost && (x + width > clientWidth || i === scrollChildren.length - 1)) {
                rightmost = bb;
              }
            }

            const cardWidth =
              buttonId === SCROLL_NEXT_CARDS ? (rightmost ? rightmost.width : 0) : leftmost ? leftmost.width : 0;
            const offsetTotal = Math.max(clientWidth - cardWidth, cardWidth);
            const offsetValue = offsetTotal * (buttonId === SCROLL_NEXT_CARDS ? 1 : -1);
            setScrollingTo(scrollLeft + offsetValue);
          }
        }
      },
    }),
    [scrollingTo, setScrollingTo, scrollContainerRef.current],
  );

  const showPagination = dotIntervals.length && !disablePagination;

  const pagination = useMemo(
    () =>
      showPagination ? (
        <PaginationWrapper mobileBreakpoint={mobileBreakpoint}>
          {dotIntervals.map((interval, i) => (
            <PaginationButton
              ariaLabel={`${pageLabel} ${i + 1}`}
              key={`pagination-dot-${i}`}
              dataAttr={{
                'data-testid': `${CONTROLLED_OVERFLOW}_pagination-button`,
                'data-mlb-test': `${CONTROLLED_OVERFLOW}_pagination-button`,
              }}
              active={i === activeDot}
              handlers={{
                onClick: () => {
                  setScrollingTo(interval);
                },
              }}
            />
          ))}
        </PaginationWrapper>
      ) : null,
    [dotIntervals, activeDot, mobileBreakpoint],
  );

  const disableButtonsFromTouch = disableButtonsOnTouch && isTouchEnabled;
  const leftDisabled = buttonsOnSide && !hideLeft;
  const rightDisabled = buttonsOnSide && !hideRight;

  const childItems = useMemo(() => {
    if (addEndInsert) {
      return [...children, <EndInsert key={`end-insert-key`} ref={endInsertRef} insertWidth={endInsertWidth} />];
    }
    return children;
  }, [children, addEndInsert, endInsertWidth]);

  return (
    <OuterWrapper className={className}>
      {!disableShadows && !buttonsOnSide && (
        <LeftShadow show={hideLeft} gradientColor={backgroundColor} dark={dark} mobileBreakpoint={mobileBreakpoint} />
      )}

      {!disableButtonsFromTouch && ((!disableArrows && hideLeft) || buttonsOnSide) && (
        <LeftButton
          handlers={buttonHandlers}
          buttonsOnSide={buttonsOnSide}
          hideOffScreen={buttonsOnSide && !showSideButtons}
          disabled={leftDisabled}
          iconColor={leftDisabled ? mlbGrey5 : linkBlue}
          styleName={ButtonStyles.DEFAULT}
          id={SCROLL_PREV_CARDS}
          iconName={leftButtonIconName}
          className={`${SCROLL_PREV_CARDS} trk-carousel-button`}
          dark={dark}
          mobileBreakpoint={mobileBreakpoint}
          buttonHeight={buttonHeight}
          dataAttr={{
            'data-testid': `${CONTROLLED_OVERFLOW}_left-button`,
            'data-mlb-test': `${CONTROLLED_OVERFLOW}_left-button`,
          }}
          ariaLabel={previousButtonLabel}
          buttonSideMargin={buttonSideMargin}
        />
      )}

      <InnerWrapper
        ref={scrollContainerRef}
        childWidth={childWidth}
        // If scrollSnapping behavior is operating at 100% (Chrome and Firefox), pause when
        // scrolling using the buttons
        pauseScrollSnapping={scrollBehaviorBrowserCapable && scrollingTo !== 0}
        numberChildren={numberOfChildren}
        dark={dark}
        buttonsOnSide={!disableButtonsFromTouch && buttonsOnSide && showSideButtons}
        buttonHeight={buttonHeight}
        buttonSideMargin={buttonSideMargin}
      >
        {childItems}
      </InnerWrapper>

      {!disableButtonsFromTouch && ((!disableArrows && hideRight) || buttonsOnSide) && (
        <RightButton
          handlers={buttonHandlers}
          disabled={rightDisabled}
          buttonsOnSide={buttonsOnSide}
          hideOffScreen={buttonsOnSide && !showSideButtons}
          iconColor={rightDisabled ? mlbGrey5 : linkBlue}
          styleName={ButtonStyles.DEFAULT}
          id={SCROLL_NEXT_CARDS}
          iconName={rightButtonIconName}
          className={`${SCROLL_NEXT_CARDS} trk-carousel-button`}
          dark={dark}
          mobileBreakpoint={mobileBreakpoint}
          buttonHeight={buttonHeight}
          dataAttr={{
            'data-testid': `${CONTROLLED_OVERFLOW}_right-button`,
            'data-mlb-test': `${CONTROLLED_OVERFLOW}_right-button`,
          }}
          ariaLabel={nextButtonLabel}
          buttonSideMargin={buttonSideMargin}
        />
      )}

      {!disableShadows && !buttonsOnSide && (
        <RightShadow show={hideRight} gradientColor={backgroundColor} dark={dark} mobileBreakpoint={mobileBreakpoint} />
      )}
      {pagination}
    </OuterWrapper>
  );
};

export default ControlledOverflow;
